import React, { useState } from 'react';
import { clsx } from 'clsx';
import { FaExpand, FaTh, FaCube, FaSyncAlt } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const FloorPreview = ({ PatternComponent, colors }) => {
  const [gridSize, setGridSize] = useState(4);
  const [viewMode, setViewMode] = useState('2d'); // '2d' or '3d'
  const [rotation, setRotation] = useState(0);
  const [tilt, setTilt] = useState(60);
  
  const tiles = Array(gridSize * gridSize).fill(null);

  const toggleViewMode = () => {
    setViewMode(prev => prev === '2d' ? '3d' : '2d');
  };

  return (
    <div className="flex flex-col h-full bg-slate-100 rounded-xl overflow-hidden border border-slate-200 relative">
      {/* Toolbar */}
      <div className="absolute top-4 left-4 z-20 flex gap-2 bg-white/90 backdrop-blur p-1.5 rounded-lg shadow-sm border border-slate-200">
        <button 
          onClick={toggleViewMode}
          className={clsx(
            "flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all",
            viewMode === '3d' ? "bg-slate-900 text-white shadow-md" : "text-slate-600 hover:bg-slate-100"
          )}
        >
          <SafeIcon icon={viewMode === '3d' ? FaTh : FaCube} className="text-sm" />
          {viewMode === '3d' ? '2D View' : '3D Room'}
        </button>
      </div>

      <div className="absolute top-4 right-4 z-20 flex gap-2 bg-white/90 backdrop-blur p-1.5 rounded-lg shadow-sm border border-slate-200">
        <button 
          onClick={() => setGridSize(4)}
          className={clsx(
            "p-2 rounded hover:bg-slate-100 transition-colors",
            gridSize === 4 ? "text-emerald-600 bg-emerald-50" : "text-slate-500"
          )}
          title="4x4 View"
        >
          <SafeIcon icon={FaTh} className="text-lg" />
        </button>
        <button 
          onClick={() => setGridSize(8)}
          className={clsx(
            "p-2 rounded hover:bg-slate-100 transition-colors",
            gridSize === 8 ? "text-emerald-600 bg-emerald-50" : "text-slate-500"
          )}
          title="8x8 View"
        >
           <SafeIcon icon={FaExpand} className="text-lg" />
        </button>
      </div>

      {/* 3D Controls Overlay */}
      <AnimatePresence>
        {viewMode === '3d' && (
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="absolute bottom-16 right-4 z-20 bg-white/90 backdrop-blur p-4 rounded-xl shadow-lg border border-slate-200 w-48 space-y-4"
          >
            <div className="space-y-2">
              <div className="flex justify-between text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                <span>Rotation</span>
                <span>{rotation}°</span>
              </div>
              <input 
                type="range" 
                min="0" 
                max="360" 
                value={rotation} 
                onChange={(e) => setRotation(parseInt(e.target.value))}
                className="w-full h-1 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-slate-900"
              />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                <span>Tilt</span>
                <span>{tilt}°</span>
              </div>
              <input 
                type="range" 
                min="30" 
                max="80" 
                value={tilt} 
                onChange={(e) => setTilt(parseInt(e.target.value))}
                className="w-full h-1 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-slate-900"
              />
            </div>
            <button 
              onClick={() => { setRotation(0); setTilt(60); }}
              className="w-full py-1.5 text-[10px] font-bold text-slate-500 hover:text-slate-900 border border-slate-200 rounded-md hover:bg-white flex items-center justify-center gap-1 transition-colors"
            >
              <SafeIcon icon={FaSyncAlt} /> RESET CAMERA
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Visualization Canvas */}
      <div className="flex-1 overflow-hidden p-8 flex items-center justify-center custom-scrollbar perspective-1000">
        <motion.div 
          layout
          className={clsx(
            "grid shadow-2xl bg-white transition-all duration-700 ease-in-out",
            viewMode === '3d' ? "ring-8 ring-white/50" : ""
          )}
          style={{ 
            gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))`,
            width: gridSize === 4 ? '400px' : '600px',
            maxWidth: '100%',
            transformStyle: 'preserve-3d',
            transform: viewMode === '3d' 
              ? `rotateX(${tilt}deg) rotateZ(${rotation}deg)` 
              : 'rotateX(0deg) rotateZ(0deg)',
          }}
          id="floor-preview-grid"
        >
            {tiles.map((_, i) => (
                <div key={i} className="aspect-square w-full">
                    <PatternComponent colors={colors} className="w-full h-full block" />
                </div>
            ))}
        </motion.div>
      </div>
      
      <div className="bg-white p-3 text-xs text-center text-slate-500 border-t border-slate-200">
        {viewMode === '3d' ? '3D Render Mode' : '2D Layout Mode'} • {gridSize}x{gridSize} Tiles
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .perspective-1000 {
          perspective: 1200px;
        }
      `}} />
    </div>
  );
};

export default FloorPreview;